import { useState, useEffect, useCallback, useRef } from 'react';
import { processoService, type Processo } from '../services/processo';

export const useProcessos = () => {
  const [processos, setProcessos] = useState<Processo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isInitialFetchDone = useRef(false);

  const fetchProcessos = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await processoService.getAll();
      setProcessos(data);
    } catch (err) {
      console.error('Erro ao buscar processos:', err);
      setError(err instanceof Error ? err.message : 'Erro ao carregar processos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isInitialFetchDone.current) {
      return;
    }

    isInitialFetchDone.current = true;
    fetchProcessos();
  }, [fetchProcessos]);

  return {
    processos,
    loading,
    error,
    refetch: fetchProcessos
  };
};

export const useProcessosBySetor = (setorId?: number) => {
  const [processos, setProcessos] = useState<Processo[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lastRequestedSetorRef = useRef<number | null>(null);

  const fetchProcessos = useCallback(async (id: number) => {
    try {
      setLoading(true);
      setError(null);
      const data = await processoService.getBySetor(id);
      setProcessos(data);
    } catch (err) {
      console.error('Erro ao buscar processos do setor:', err);
      setError(err instanceof Error ? err.message : 'Erro ao carregar processos do setor');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (setorId) {
      if (lastRequestedSetorRef.current === setorId) {
        return;
      }

      lastRequestedSetorRef.current = setorId;
      fetchProcessos(setorId);
    } else {
      setProcessos([]);
      lastRequestedSetorRef.current = null;
    }
  }, [setorId, fetchProcessos]);

  return {
    processos,
    loading,
    error,
    refetch: setorId 
      ? () => {
          lastRequestedSetorRef.current = setorId;
          return fetchProcessos(setorId);
        }
      : () => {}
  };
};